import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * Invoice activity audit trail and the warnings raised by a deactivation.
 *
 * Every row comes from the tenant-scoped `sale/activity` read; nothing is
 * inferred client-side. The accounting warning is rendered verbatim because it
 * is deliberately honest — it asks a human to reverse the invoice externally
 * and never claims the portal already did so.
 */
const EVENT_LABELS = {
  invoice_deactivated: "Invoice deactivated",
  invoice_restored: "Invoice reactivated",
  voucher_restored: "Voucher balance restored",
  "invoice.sale_date_changed": "Sale date changed",
  "invoice.deactivated": "Invoice deactivated",
  "invoice.reactivated": "Invoice reactivated",
  "invoice.updated": "Invoice updated",
  "invoice.content_changed": "Invoice content changed",
  "invoice.created": "Invoice created",
  "invoice.payment_recorded": "Payment recorded",
  "invoice.refund_recorded": "Refund recorded",
};

const eventLabel = (event) => {
  const key = String(event || "").trim();
  if (!key) return "Activity";
  if (EVENT_LABELS[key]) return EVENT_LABELS[key];
  // An unknown code is shown rather than hidden, so a new server event is
  // never silently lost from the timeline.
  return key.replace(/[_-]+/g, " ");
};

const detailSummary = (detail) => {
  if (!detail || typeof detail !== "object") return "";
  const count = Number(detail.count ?? detail.restored_voucher_count);
  if (Number.isFinite(count) && count > 0) return `${count} voucher balance${count === 1 ? "" : "s"}`;
  const text = detail.message ?? detail.note;
  return typeof text === "string" ? text : "";
};

const changeSummary = (entry) => {
  const action = entry?.action;
  if (action === "invoice.payment_recorded" || action === "invoice.refund_recorded") {
    const amount = Number(entry?.after_state?.amount_minor);
    const method = action === "invoice.payment_recorded" ? entry?.after_state?.paid_by : "";
    return Number.isFinite(amount) ? [method, (amount / 100).toFixed(2)].filter(Boolean).join(" · ") : "";
  }
  const before = entry?.before_state?.sale_date;
  const after = entry?.after_state?.sale_date;
  if (before && after) return `${before} → ${after}`;
  const beforeTotal = Number(entry?.before_state?.total_minor);
  const afterTotal = Number(entry?.after_state?.total_minor);
  if (Number.isFinite(beforeTotal) && Number.isFinite(afterTotal) && beforeTotal !== afterTotal) {
    return `${(beforeTotal / 100).toFixed(2)} → ${(afterTotal / 100).toFixed(2)}`;
  }
  return "";
};

const InvoiceActivityTimeline = ({ activity = [], warnings = [], restoredVoucherCount = 0, status, onRetry }) => {
  const { t } = useTranslation();
  const entries = Array.isArray(activity) ? activity : [];
  const alerts = Array.isArray(warnings) ? warnings : [];
  const count = Number(restoredVoucherCount) || 0;

  if (!entries.length && !alerts.length && count <= 0 && status !== "loading" && status !== "failed") return null;

  return (
    <section className="invoice-activity" aria-label={t("Invoice activity")}>
      {status === "loading" && <p role="status">{t("Loading invoice activity")}</p>}
      {status === "failed" && <div role="alert">
        {t("Unable to load invoice activity.")}
        {onRetry && <button type="button" className="btn btn-link btn-sm" onClick={onRetry}>{t("Retry activity")}</button>}
      </div>}
      {alerts.map((warning, index) => (
        <p className="alert alert-warning" role="alert" key={warning?.code || index}>
          {warning?.message || t("This invoice change needs a manual follow-up.")}
        </p>
      ))}
      {count > 0 && (
        <p className="invoice-activity-vouchers">
          {count === 1
            ? t("1 voucher balance restored")
            : t("{{count}} voucher balances restored").replace("{{count}}", String(count))}
        </p>
      )}
      {entries.length > 0 && (
        <ol className="invoice-activity-timeline list-unstyled">
          {entries.map((entry, index) => (
            <li key={entry?.id ?? index}>
              <span className="invoice-activity-event">{t(eventLabel(entry?.action || entry?.event))}</span>
              {detailSummary(entry?.detail) && (
                <span className="invoice-activity-detail"> · {detailSummary(entry?.detail)}</span>
              )}
              {changeSummary(entry) && <span className="invoice-activity-detail"> · {changeSummary(entry)}</span>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

InvoiceActivityTimeline.propTypes = {
  activity: PropTypes.arrayOf(PropTypes.object),
  warnings: PropTypes.arrayOf(PropTypes.object),
  restoredVoucherCount: PropTypes.number,
  status: PropTypes.string,
  onRetry: PropTypes.func,
};

export default InvoiceActivityTimeline;
